import { useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Checkbox } from '@/components/ui/checkbox';
import { AlertDialog, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '@/components/ui/alert-dialog';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import { toast } from '@/hooks/use-toast';
import { AlertTriangle, Loader2 } from 'lucide-react';
import { ClosurePreviewLists, formatDeletionDate, type ClosurePreview } from '@/components/account/ClosurePreview';

interface AccountClosurePreview extends ClosurePreview {
  sole_owner_shops?: Array<{ shop_id: string; name: string }>;
  shop_closure?: ClosurePreview;
}

/**
 * Account Settings: Danger Zone — close my account (Passport ToS §10(a): "You may close your account through the App
 * settings").
 *
 * Core-100 R4 (OD-27): one server transaction, `close_my_account` (migration 20270206013100). The dialog shows the
 * server's dry run — exactly what the deletion catalogue will delete 30 days later, what awaits a legal decision and
 * what is kept — instead of the earlier fixed copy, which promised a phone number was removed (none is stored on the
 * profile; the sign-in phone is now cleared) and that business records were anonymised on account closure (they are
 * not: a shop's records are de-identified only when the SHOP closes). A sole owner can close their shop(s) with the
 * account, in the same transaction; otherwise the server refuses with LAST_OWNER_ACTIVE_SHOP.
 */
export function AccountDangerZone() {
  const { signOut } = useAuth();
  const [isOpen, setIsOpen] = useState(false);
  const [confirmText, setConfirmText] = useState('');
  const [closeShops, setCloseShops] = useState(false);
  const [preview, setPreview] = useState<AccountClosurePreview | null>(null);
  const [previewError, setPreviewError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isClosing, setIsClosing] = useState(false);

  const soleOwnerShops = preview?.sole_owner_shops ?? [];
  const needsShopChoice = soleOwnerShops.length > 0;
  const canConfirm = confirmText === 'CLOSE' && !isClosing && preview !== null && (!needsShopChoice || closeShops);

  const open = async () => {
    setIsOpen(true);
    setIsLoading(true);
    setPreviewError(null);
    const { data, error } = await supabase.rpc('close_my_account' as never, { p_dry_run: true, p_close_owned_shops: false } as never);
    setIsLoading(false);
    if (error) {
      setPreview(null);
      setPreviewError('What closing your account would do could not be loaded. Nothing has changed.');
      return;
    }
    setPreview(data as unknown as AccountClosurePreview);
  };

  const reset = () => {
    setIsOpen(false);
    setConfirmText('');
    setCloseShops(false);
    setPreview(null);
    setPreviewError(null);
  };

  const handleClose = async () => {
    if (!canConfirm) return;
    setIsClosing(true);
    try {
      const { data, error } = await supabase.rpc('close_my_account' as never, { p_dry_run: false, p_close_owned_shops: closeShops } as never);
      if (error) throw error;
      const due = formatDeletionDate((data as unknown as { deletion_due_at?: string })?.deletion_due_at);
      toast({
        title: 'Account closed',
        description: due ? `Your account is closed. The listed personal information will be deleted on or after ${due}.` : 'Your account is closed.',
      });
      reset();
      setTimeout(() => signOut(), 1500);
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : (err as { message?: string })?.message;
      toast({
        title: 'Your account was not closed',
        description: message?.includes('LAST_OWNER_ACTIVE_SHOP')
          ? 'You are the only owner of an active shop. Choose to close the shop with your account, or add another owner first.'
          : 'Nothing has changed. Please try again.',
        variant: 'destructive',
      });
    } finally {
      setIsClosing(false);
    }
  };

  return (
    <>
      <Card className="border-destructive/30">
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-destructive">
            <AlertTriangle className="h-5 w-5" />
            Danger Zone
          </CardTitle>
          <CardDescription>
            Irreversible actions that affect your account
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex items-center justify-between p-3 rounded-lg border border-destructive/20">
            <div>
              <p className="text-sm font-medium">Close Account</p>
              <p className="text-sm text-muted-foreground">
                Close your account and leave all shops. Your Passport is hidden from every shop at once. You are
                signed out everywhere, and you will see exactly what is deleted, and when, before you confirm.
              </p>
            </div>
            <Button variant="destructive" size="sm" onClick={open} data-testid="close-account-open">
              Close account
            </Button>
          </div>
        </CardContent>
      </Card>

      <AlertDialog open={isOpen} onOpenChange={(o) => { if (!o) reset(); }}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="text-destructive">Close your account</AlertDialogTitle>
            <AlertDialogDescription asChild>
              <div className="space-y-3 text-sm text-muted-foreground">
                {isLoading && <p className="flex items-center gap-2"><Loader2 className="h-4 w-4 animate-spin" /> Loading what this will do…</p>}
                {previewError && <p className="text-destructive" data-testid="close-account-preview-error">{previewError}</p>}
                {preview && (
                  <>
                    <p>
                      Your account closes immediately: you leave every shop, your Passport is hidden from everyone, and
                      every signed-in session ends. Until the deletion date, OptiBay support can restore your account if
                      you ask (your shop memberships are not restored).
                    </p>
                    <ClosurePreviewLists preview={preview} />
                    {needsShopChoice && (
                      <div className="rounded-md border border-destructive/30 p-3 space-y-2" data-testid="close-account-sole-owner">
                        <p className="font-medium text-foreground">
                          You are the only owner of {soleOwnerShops.map((s) => s.name).join(', ')}.
                        </p>
                        <p>
                          To close your account you must also close {soleOwnerShops.length === 1 ? 'this shop' : 'these shops'}.
                          The shop&apos;s VINs, license plates and customer contact details are then deleted 30 days later.
                        </p>
                        {preview.shop_closure && <ClosurePreviewLists preview={preview.shop_closure} deleteTitle="Deleted from the shop 30 days after it closes:" />}
                        <label className="flex items-center gap-2 text-foreground">
                          <Checkbox checked={closeShops} onCheckedChange={(v) => setCloseShops(v === true)} data-testid="close-account-close-shops" />
                          Close {soleOwnerShops.length === 1 ? 'the shop' : 'the shops'} with my account
                        </label>
                      </div>
                    )}
                    <p className="font-medium text-foreground">
                      Type <span className="font-mono bg-muted px-1.5 py-0.5 rounded">CLOSE</span> to confirm:
                    </p>
                    <Input
                      value={confirmText}
                      onChange={(e) => setConfirmText(e.target.value)}
                      placeholder="Type CLOSE"
                      className="font-mono"
                      data-testid="close-account-confirm-text"
                    />
                  </>
                )}
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={isClosing}>Cancel</AlertDialogCancel>
            <Button variant="destructive" onClick={handleClose} disabled={!canConfirm} data-testid="close-account-confirm">
              {isClosing && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              Close My Account
            </Button>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
